'use client';
import { getDividedArray, setEncodetext } from '@/services/helperFunctions';
import { Issuer, issuerBondsCountData } from '@/types/issuerIndexTypes';
import Image from 'next/image';
import Link from 'next/link';
import { useCallback, useDeferredValue, useMemo, useState } from 'react';
import wave from '../../../public/img/waves.png';
import IssuersIndexNavigation from './IssuersIndexNavigation';
import SearchInput from './SearchInput';

interface IssuerIndexPageProps {
    IssuerIDUpper: string;
    issuersList: Issuer[];
    issuerFirstletterListData?: issuerBondsCountData[];
}

type issuerFirstletterListDataToMapType = {
    issuer: string;
    issuer_name: string[];
};

const HighlightedText = ({ text, highlight }: { text: string; highlight: string }) => {
    if (!highlight) return <>{text}</>;

    const regex = new RegExp(`(${highlight})`, 'gi');
    const parts = text.split(regex);

    return (
        <>
            {parts.map((part, index) =>
                regex.test(part) ? (
                    <span
                        key={index}
                        style={{
                            backgroundColor: '#fbe541ff',
                            borderRadius: '4px',
                        }}
                    >
                        {part}
                    </span>
                ) : (
                    <span key={index}>{part}</span>
                )
            )}
        </>
    );
};

const IssuerIndexPage = ({ IssuerIDUpper, issuersList, issuerFirstletterListData }: IssuerIndexPageProps): JSX.Element => {
    const [issuerSearchInput, setIssuerSearchInput] = useState<string>('');

    // Memoize the handler function to prevent recreating it on each render
    const handleIssuerFirstletterListData = useCallback(() => {
        let res: issuerFirstletterListDataToMapType[] = [];

        if (!issuerFirstletterListData) return res;

        issuerFirstletterListData.forEach((issuer) => {
            const issuerIndex = res.findIndex((x) => x?.issuer === issuer.issuer);

            if (issuerIndex !== -1) {
                res[issuerIndex] = {
                    issuer: res[issuerIndex].issuer,
                    issuer_name: [...res[issuerIndex].issuer_name, issuer.issuer_name],
                };
            } else {
                res.push({
                    issuer: issuer.issuer,
                    issuer_name: [issuer.issuer_name],
                });
            }
        });

        return res;
    }, [issuerFirstletterListData]);

    // Use the memoized handler directly
    const issuerFirstletterListDataToMap = useMemo(() => handleIssuerFirstletterListData(), [handleIssuerFirstletterListData]);

    // Group related calculations in one useMemo to avoid multiple unnecessary recalculations
    const issuerNavigationInfo = useMemo(() => {
        if (!issuerFirstletterListDataToMap?.length) {
            return {
                currentIssuerIndex: -1,
                prevIssuerIndex: -1,
                prevIssuerLink: '',
                nextIssuerIndex: -1,
                nextIssuerLink: '',
            };
        }

        const currentIssuerIndex = issuerFirstletterListDataToMap.findIndex((item) => item?.issuer === IssuerIDUpper);

        const prevIssuerIndex = currentIssuerIndex > 0 ? currentIssuerIndex - 1 : -1;
        const prevIssuerLink = prevIssuerIndex >= 0 ? `/issuers/${issuerFirstletterListDataToMap[prevIssuerIndex]?.issuer}` : '';

        const nextIssuerIndex = currentIssuerIndex >= 0 ? currentIssuerIndex + 1 : -1;
        const nextIssuerLink =
            nextIssuerIndex < issuerFirstletterListDataToMap.length
                ? `/issuers/${issuerFirstletterListDataToMap[nextIssuerIndex]?.issuer}`
                : '';

        return {
            currentIssuerIndex,
            prevIssuerIndex,
            prevIssuerLink,
            nextIssuerIndex,
            nextIssuerLink,
        };
    }, [issuerFirstletterListDataToMap, IssuerIDUpper]);

    // Extract values from the memoized object for use in your component
    const { prevIssuerLink, nextIssuerLink } = issuerNavigationInfo;

    const deferredSearch = useDeferredValue(issuerSearchInput);

    // Memoize filtered issuer list
    const issuerList = useMemo(() => {
        if (!issuersList) return [];

        return deferredSearch.length
            ? issuersList.filter((item) => item.issuer.toLowerCase().includes(deferredSearch.toLowerCase()))
            : issuersList;
    }, [issuersList, deferredSearch]);

    // Memoize the divided array calculation
    const issuerListDivided = useMemo(() => getDividedArray(issuerList ?? [], 4), [issuerList]);
    return (
        <div className="rounded shadow p-3 mb-4">
            <div className="row align-items-sm-center flex-lg-row-reverse justify-content-lg-end">
                <SearchInput IssuerId={IssuerIDUpper} issuerSearchInput={issuerSearchInput} setIssuerSearchInput={setIssuerSearchInput} />
                <h1 className="issuer-heading my-3 col-lg-5 ms-lg-2">
                    {IssuerIDUpper} Alphabetical issuers list - {issuersList.length}
                </h1>
                {!!issuerFirstletterListData?.length && (
                    <IssuersIndexNavigation prevIssuerLink={prevIssuerLink} nextIssuerLink={nextIssuerLink} />
                )}
            </div>

            <div className="bg-wave-image">
                <Image loading="eager" src={wave} alt="wave-bg" width={'2000'} height={'404'} />
            </div>

            <div className="row gap-1">
                <div
                    className="shadow-lg border pt-2 rounded col-2 col-xxl-1 px-2 d-flex flex-column flex-lg-wrap align-items-center gap-2 position-lg-sticky"
                    style={{ maxHeight: '74vh', width: '10.5%', minWidth: 'fit-content', top: '11%' }}
                >
                    {issuerFirstletterListDataToMap?.map((_, index) => (
                        <Link
                            href={`/issuers/${_.issuer}`}
                            className={`btn-primary-cus no-underline font-weight-600 text-sm shadow-sm rounded-circle pt-1 text-center cursor-pointer align-content-center ${IssuerIDUpper?.toLowerCase() === _.issuer.toLowerCase() ? 'shibui-bg text-white' : 'bg-white'}`}
                            key={index}
                            style={{ width: 28, minHeight: 28 }}
                        >
                            {_.issuer}
                        </Link>
                    ))}
                </div>
                <div className="col-10 row" style={{ position: 'relative', zIndex: 0, maxHeight: '100%' }}>
                    {issuerListDivided?.length && !!issuerListDivided[0].length ? (
                        issuerListDivided?.map((item, index: number) => (
                            <div key={index} className="col-12 col-lg-3 d-flex flex-column gap-2 p-0 px-1">
                                {item.map((item_, index: number) => (
                                    <div
                                        className="issuer-div position-relative mt-2 mt-lg-0 font-size-12 px-2"
                                        style={{ zIndex: 10 }}
                                        key={index}
                                    >
                                        <Link
                                            href={'/issuer-bonds/' + setEncodetext(item_.cleaned_issuername)}
                                            prefetch={false}
                                            className="p-2 my-1 no-underline text-xs cursor-pointer material-shadow rounded col-12 flex-grow-1 d-flex align-items-center align-content-around justify-content-between fw-medium gap-2"
                                        >
                                            <span
                                                className="issuer ps-xl-1 col-10 flex-grow-1 overflow-hidden text-nowrap"
                                                style={{ minHeight: 26, textOverflow: 'ellipsis' }}
                                            >
                                                <HighlightedText
                                                    key={item_.cleaned_issuername}
                                                    text={item_.cleaned_issuername}
                                                    highlight={issuerSearchInput}
                                                />
                                            </span>
                                            <span
                                                className="col bonds-count rounded-circle font-size-10 d-flex align-items-center justify-content-center "
                                                style={{ minHeight: 24, aspectRatio: '1 / 1' }}
                                            >
                                                {item_.bonds_count}
                                            </span>
                                        </Link>
                                    </div>
                                ))}
                            </div>
                        ))
                    ) : (
                        <div className="d-flex align-items-center justify-content-center ">
                            <p className="text-center">No issuers found</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default IssuerIndexPage;
